import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sdk-documentation',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './sdk-documentation.component.html',
  styleUrls: ['./sdk-documentation.component.scss'],
})
export class SdkDocumentationComponent implements OnInit, OnDestroy {
  private translateService = inject(TranslateService);

  activeSection: string = 'overview';
  activePlatform: string = 'web';
  expandedEndpoints: Set<string> = new Set();
  private queryParamSub: Subscription | null = null;

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.queryParamSub = this.route.queryParams.subscribe(params => {
      if (params['section']) {
        this.activeSection = params['section'];
      } else {
        this.activeSection = 'overview';
      }
    });
  }

  ngOnDestroy(): void {
    if (this.queryParamSub) {
      this.queryParamSub.unsubscribe();
    }
  }

  sections = [
    { id: 'overview', label: 'Overview', icon: 'book' },
    { id: 'getting-started', label: 'Getting Started', icon: 'rocket' },
    { id: 'configuration', label: 'Configuration', icon: 'settings' },
    { id: 'authentication', label: 'Authentication', icon: 'lock' },
    { id: 'api-reference', label: 'API Reference', icon: 'code' },
    { id: 'data-models', label: 'Data Models', icon: 'database' },
    { id: 'error-handling', label: 'Error Handling', icon: 'alert' },
    { id: 'platform-guides', label: 'Platform Guides', icon: 'devices' },
  ];

  platforms = [
    { id: 'web', label: 'Web/JavaScript', icon: 'js' },
    { id: 'react-native', label: 'React Native', icon: 'react' },
    { id: 'flutter', label: 'Flutter', icon: 'flutter' },
    { id: 'ios-swift', label: 'iOS (Swift)', icon: 'apple' },
    { id: 'ios-objc', label: 'iOS (Obj-C)', icon: 'apple' },
    { id: 'android-kotlin', label: 'Android (Kotlin)', icon: 'android' },
    { id: 'android-java', label: 'Android (Java)', icon: 'android' },
  ];

  apiEndpoints = [
    {
      id: 'initialize',
      method: 'POST',
      path: '/sdk/app/initialize',
      description: 'Initialize SDK session and obtain JWE encrypted authentication token',
      category: 'Authentication',
      headers: [
        { name: 'Authorization', value: 'Bearer {API_KEY}', required: true },
        { name: 'X-App-Token', value: '{APPLICATION_ID}', required: true },
        { name: 'X-User', value: '{"email":"user@example.com"} OR {"phone_number":"+923001234567"}', required: true },
      ],
      response: {
        success: true,
        code: 'SDK-INIT-SUCCESS',
        message: 'SDK initialized successfully',
        token: 'eyJhbGciOiJBMjU2S1ciLCJlbmMiOiJBMjU2Q0JDLUhTNTEyIn0...',
        user: {
          id: 123,
          email: 'user@example.com',
          name: 'John Doe',
          phone_number: '+923001234567',
          total_points: 500,
          created_at: '2024-01-15T10:30:00.000Z'
        },
        application: {
          id: 1,
          name: 'My App',
          status: 'active'
        }
      },
    },
    {
      id: 'user-sync',
      method: 'POST',
      path: '/sdk/app/user/sync',
      description: 'Smart user create/update with automatic change detection (only updates changed fields)',
      category: 'User Management',
      body: {
        name: 'John Doe',
        email: 'john@example.com',
        phone_number: '+923147940690',
        gender: 'male',
        country: 'PK',
        dob: '1995-05-15',
        image_url: 'https://example.com/avatar.jpg',
        interested_in: ['sports', 'technology'],
        policy_accept: true
      },
      response: {
        success: true,
        code: 'SDK-USER-UPDATED',
        message: 'User profile updated successfully',
        data: {
          user_id: 123,
          updated_fields: ['name', 'gender', 'country'],
          user: {
            id: 123,
            name: 'John Doe',
            email: 'john@example.com',
            phone_number: '+923147940690',
            gender: 'male',
            country: 'PK'
          }
        },
      },
    },
    {
      id: 'user-profile',
      method: 'GET',
      path: '/sdk/app/user/profile',
      description: 'Retrieve authenticated user profile with points and campaign stats',
      category: 'User Management',
      response: {
        success: true,
        data: {
          id: 123,
          name: 'John Doe',
          email: 'john@example.com',
          phone_number: '+923147940690',
          gender: 'male',
          country: 'PK',
          dob: '1995-05-15',
          image_url: 'https://example.com/avatar.jpg',
          total_points: 2500,
          contests_participated: 15,
          contests_won: 3,
          Verified: true,
          created_at: '2024-01-15T10:30:00.000Z'
        },
      },
    },
    {
      id: 'contest-participate',
      method: 'POST',
      path: '/sdk/app/contest/participate',
      description: 'Register user in a campaign (access_code required for private campaigns)',
      category: 'Campaign Management',
      body: { contest_id: 1, access_code: 'ABC123' },
      response: {
        success: true,
        code: 'SDK-CONTEST-JOINED',
        message: 'Successfully registered in campaign',
        data: {
          participant_id: 456,
          contest: {
            id: 1,
            name: 'Summer Challenge 2024',
            type: 'public',
            start_date: '2024-06-01T00:00:00.000Z',
            end_date: '2024-06-30T23:59:59.000Z'
          },
          user_progress: {
            current_level: 1,
            completed_levels: 0,
            total_levels: 5,
            points_earned: 0
          }
        },
      },
    },
    {
      id: 'contest-recently',
      method: 'GET',
      path: '/sdk/app/contest/recently',
      description: 'List recently active campaigns (paginated)',
      category: 'Campaign Management',
      params: [
        { name: 'page', type: 'number', description: 'Page number (default: 1)' },
        { name: 'per_page', type: 'number', description: 'Items per page (default: 10)' },
      ],
      response: {
        success: true,
        data: [
          {
            id: 1,
            name: 'Summer Challenge 2024',
            arabic_name: 'تحدي الصيف 2024',
            details: 'Complete exciting levels to win prizes',
            type: 'public',
            status: 'approved',
            no_of_users: 1500,
            start_date: '2024-06-01T00:00:00.000Z',
            end_date: '2024-06-30T23:59:59.000Z',
            banners: ['https://cdn.luqta.com/contests/banner1.jpg'],
            View_Type: 'Recent Campaign'
          }
        ],
        pagination: { page: 1, per_page: 10, total: 100, total_pages: 10 },
      },
    },
    {
      id: 'contest-all',
      method: 'GET',
      path: '/sdk/app/contest/all',
      description: 'Get all available campaigns for the application',
      category: 'Campaign Management',
      response: {
        success: true,
        data: [
          {
            id: 1,
            name: 'Summer Challenge 2024',
            arabic_name: 'تحدي الصيف 2024',
            details: 'Complete exciting levels to win prizes',
            type: 'public',
            status: 'approved',
            no_of_users: 1500,
            format_of_contest: 'levels',
            start_date: '2024-06-01T00:00:00.000Z',
            end_date: '2024-06-30T23:59:59.000Z',
            prize_announce_date: '2024-07-05T00:00:00.000Z',
            banners: ['https://cdn.luqta.com/contests/banner1.jpg']
          }
        ]
      },
    },
    {
      id: 'contest-premium',
      method: 'GET',
      path: '/sdk/app/contest/premium',
      description: 'Get VIP/Premium campaigns',
      category: 'Campaign Management',
      response: {
        success: true,
        data: [
          {
            id: 5,
            name: 'VIP Exclusive Challenge',
            arabic_name: 'تحدي VIP الحصري',
            type: 'private',
            View_Type: 'VIP',
            status: 'approved',
            no_of_users: 250,
            banners: ['https://cdn.luqta.com/contests/vip-banner.jpg']
          }
        ]
      },
    },
    {
      id: 'contest-trending',
      method: 'GET',
      path: '/sdk/app/contest/trending',
      description: 'Get currently trending campaigns by participation',
      category: 'Campaign Management',
      response: {
        success: true,
        data: [
          {
            id: 3,
            name: 'Trending Challenge',
            type: 'public',
            status: 'approved',
            no_of_users: 5000,
            trending_score: 95,
            banners: ['https://cdn.luqta.com/contests/trending.jpg']
          }
        ]
      },
    },
    {
      id: 'contest-details',
      method: 'GET',
      path: '/sdk/app/contest/details-progress',
      description: 'Get complete campaign info with levels and user progress',
      category: 'Campaign Management',
      params: [{ name: 'contest_id', type: 'number', description: 'Campaign ID' }],
      response: {
        success: true,
        data: {
          contest: {
            id: 1,
            name: 'Summer Challenge 2024',
            arabic_name: 'تحدي الصيف 2024',
            details: 'Complete exciting levels to win prizes',
            type: 'public',
            status: 'approved',
            start_date: '2024-06-01T00:00:00.000Z',
            end_date: '2024-06-30T23:59:59.000Z'
          },
          levels: [
            {
              id: 1,
              name: 'Level 1: Getting Started',
              level_type: 'text',
              points: 100,
              description: 'Answer the question correctly',
              is_completed: true
            },
            {
              id: 2,
              name: 'Level 2: QR Hunt',
              level_type: 'qr',
              points: 200,
              description: 'Scan the QR code',
              is_completed: false
            }
          ],
          user_progress: {
            is_participating: true,
            current_level: 2,
            completed_levels: 1,
            total_levels: 5,
            points_earned: 100,
            completion_percentage: 20
          }
        },
      },
    },
    {
      id: 'contest-history',
      method: 'GET',
      path: '/sdk/app/contest/history',
      description: "User's campaign participation history with results",
      category: 'Campaign Management',
      response: {
        success: true,
        data: [
          {
            contest_id: 1,
            contest_name: 'Spring Challenge 2024',
            participated_at: '2024-03-15T10:30:00.000Z',
            status: 'completed',
            levels_completed: 5,
            total_levels: 5,
            points_earned: 500,
            rank: 15,
            prize_won: 'Gift Card $50'
          }
        ]
      },
    },
    {
      id: 'level-complete',
      method: 'POST',
      path: '/sdk/app/contest/level/complete',
      description: 'Complete a level (supports text, QR code, and link validation types)',
      category: 'Levels',
      body: {
        level_id: 1,
        answer: 'user_answer',
        type: 'text'
      },
      response: {
        success: true,
        code: 'SDK-LEVEL-COMPLETED',
        message: 'Level completed successfully',
        data: {
          level_id: 1,
          points_earned: 100,
          total_points: 600,
          next_level: {
            id: 2,
            name: 'Level 2: QR Hunt',
            level_type: 'qr',
            points: 200
          },
          contest_progress: {
            completed_levels: 2,
            total_levels: 5,
            completion_percentage: 40
          }
        }
      },
    },
    {
      id: 'level-complete-image',
      method: 'POST',
      path: '/sdk/app/contest/level/complete-image-type',
      description: 'Complete an image-based level (upload image for verification)',
      category: 'Levels',
      body: {
        level_id: 1,
        image: 'data:image/jpeg;base64,/9j/4AAQSkZJRg...'
      },
      response: {
        success: true,
        code: 'SDK-LEVEL-COMPLETED',
        message: 'Image level completed successfully',
        data: {
          level_id: 1,
          points_earned: 150,
          verification_status: 'approved',
          image_url: 'https://cdn.luqta.com/submissions/img_123.jpg'
        }
      },
    },
    {
      id: 'quiz-start',
      method: 'GET',
      path: '/sdk/app/quiz/start',
      description: 'Start a new quiz attempt and get all questions',
      category: 'Quiz System',
      params: [{ name: 'quiz_id', type: 'number', description: 'Quiz ID' }],
      response: {
        success: true,
        data: {
          attempt_id: 789,
          quiz: {
            id: 1,
            name: 'General Knowledge Quiz',
            time_limit: 300,
            passing_score: 70,
            total_questions: 10
          },
          questions: [
            {
              id: 1,
              question: 'What is the capital of France?',
              options: [
                { id: 1, text: 'London' },
                { id: 2, text: 'Paris' },
                { id: 3, text: 'Berlin' },
                { id: 4, text: 'Madrid' }
              ],
              points: 10
            }
          ],
          started_at: '2024-06-15T10:30:00.000Z'
        },
      },
    },
    {
      id: 'quiz-answer',
      method: 'POST',
      path: '/sdk/app/quiz/attempt/answer',
      description: 'Submit answer to a quiz question',
      category: 'Quiz System',
      body: {
        attempt_id: 789,
        question_id: 1,
        option_id: 2
      },
      response: {
        success: true,
        data: {
          is_correct: true,
          correct_option_id: 2,
          points_earned: 10,
          explanation: 'Paris is the capital and largest city of France.',
          questions_remaining: 9
        }
      },
    },
    {
      id: 'quiz-submit',
      method: 'POST',
      path: '/sdk/app/quiz/attempt/submit',
      description: 'Submit completed quiz and get final results',
      category: 'Quiz System',
      body: { attempt_id: 789 },
      response: {
        success: true,
        data: {
          attempt_id: 789,
          score: 85,
          percentage: 85,
          grade: 'A',
          passed: true,
          time_taken: '5m 30s',
          correct_count: 17,
          incorrect_count: 3,
          unanswered_count: 0,
          points_earned: 170,
          ranking: {
            position: 5,
            total_participants: 150
          }
        },
      },
    },
    {
      id: 'notifications',
      method: 'GET',
      path: '/sdk/app/contest/get-notification',
      description: 'Get user notifications (campaigns, prizes, announcements)',
      category: 'Notifications',
      response: {
        success: true,
        data: [
          {
            id: 1,
            type: 'contest_started',
            title: 'New Campaign Available!',
            message: 'Summer Challenge 2024 is now live. Participate now!',
            contest_id: 1,
            is_read: false,
            created_at: '2024-06-01T00:00:00.000Z'
          },
          {
            id: 2,
            type: 'prize_won',
            title: 'Congratulations!',
            message: 'You won a Gift Card in Spring Challenge!',
            prize_details: { name: 'Gift Card $50', value: 50 },
            is_read: true,
            created_at: '2024-05-15T10:00:00.000Z'
          }
        ],
        unread_count: 5
      },
    },
    {
      id: 'notifications-read',
      method: 'POST',
      path: '/sdk/app/contest/mark-notifications-read',
      description: 'Mark one or more notifications as read',
      category: 'Notifications',
      body: { notification_ids: [1, 2, 3] },
      response: {
        success: true,
        message: 'Notifications marked as read',
        data: {
          marked_count: 3,
          remaining_unread: 2
        }
      },
    },
    {
      id: 'reward-list',
      method: 'GET',
      path: '/sdk/app/reward-list',
      description: 'Get available rewards/prizes that users can redeem',
      category: 'Rewards',
      response: {
        success: true,
        data: [
          {
            id: 1,
            name: 'Amazon Gift Card',
            description: '$50 Amazon Gift Card',
            points_required: 5000,
            image_url: 'https://cdn.luqta.com/rewards/amazon.jpg',
            category: 'Gift Cards',
            available_quantity: 100,
            is_active: true
          },
          {
            id: 2,
            name: 'iPhone 15',
            description: 'Apple iPhone 15 128GB',
            points_required: 50000,
            image_url: 'https://cdn.luqta.com/rewards/iphone.jpg',
            category: 'Electronics',
            available_quantity: 5,
            is_active: true
          }
        ]
      },
    },
    {
      id: 'user-earnings',
      method: 'GET',
      path: '/sdk/app/contest/user-earnings',
      description: "Get user's total earnings, points history, and won prizes",
      category: 'Rewards',
      response: {
        success: true,
        data: {
          total_points: 2500,
          redeemable_points: 2000,
          pending_points: 500,
          lifetime_earnings: 5000,
          rewards_redeemed: [
            {
              id: 1,
              reward_name: 'Amazon Gift Card $25',
              points_spent: 2500,
              redeemed_at: '2024-05-01T10:00:00.000Z',
              status: 'delivered'
            }
          ],
          prizes_won: [
            {
              contest_id: 1,
              contest_name: 'Spring Challenge 2024',
              prize_name: 'Gift Card $50',
              won_at: '2024-04-15T18:00:00.000Z',
              status: 'claimed'
            }
          ],
          points_history: [
            {
              id: 1,
              type: 'earned',
              amount: 100,
              source: 'Level Completion',
              contest_name: 'Summer Challenge',
              created_at: '2024-06-15T10:30:00.000Z'
            }
          ]
        }
      },
    },
  ];

  errorCodes = [
    { category: 'Authentication', codes: [
      { code: 'SDK-AUTH-001', message: 'Missing API key' },
      { code: 'SDK-AUTH-002', message: 'Invalid API key' },
      { code: 'SDK-AUTH-003', message: 'API key expired' },
      { code: 'SDK-AUTH-004', message: 'Missing authorization header' },
      { code: 'SDK-AUTH-005', message: 'Invalid authorization format' },
    ]},
    { category: 'Application', codes: [
      { code: 'SDK-APP-001', message: 'Application not found' },
      { code: 'SDK-APP-002', message: 'Application is inactive' },
    ]},
    { category: 'Client', codes: [
      { code: 'SDK-CLIENT-001', message: 'Client account not found' },
      { code: 'SDK-CLIENT-002', message: 'Client account not verified' },
      { code: 'SDK-CLIENT-003', message: 'Client account disabled' },
      { code: 'SDK-CLIENT-004', message: 'Client account suspended' },
    ]},
    { category: 'Subscription', codes: [
      { code: 'SDK-SUB-001', message: 'No active subscription' },
      { code: 'SDK-SUB-002', message: 'Subscription expired' },
      { code: 'SDK-SUB-003', message: 'Payment pending' },
      { code: 'SDK-SUB-004', message: 'Subscription cancelled' },
    ]},
    { category: 'Plan', codes: [
      { code: 'SDK-PLAN-001', message: 'Plan not found' },
      { code: 'SDK-PLAN-002', message: 'SDK feature not enabled in plan' },
    ]},
    { category: 'User', codes: [
      { code: 'SDK-USER-001', message: 'User sync required' },
      { code: 'SDK-USER-CREATED', message: 'New user created successfully' },
      { code: 'SDK-USER-UPDATED', message: 'User profile updated' },
      { code: 'SDK-USER-NO-CHANGES', message: 'No changes detected' },
    ]},
    { category: 'Token', codes: [
      { code: 'SDK-TOKEN-001', message: 'Missing token' },
      { code: 'SDK-TOKEN-002', message: 'Invalid token format' },
      { code: 'SDK-TOKEN-003', message: 'Token expired' },
      { code: 'SDK-TOKEN-004', message: 'Token decryption failed' },
      { code: 'SDK-TOKEN-005', message: 'Token validation failed' },
      { code: 'SDK-TOKEN-006', message: 'Token data mismatch' },
    ]},
    { category: 'Input', codes: [
      { code: 'SDK-INPUT-001', message: 'Invalid input format' },
      { code: 'SDK-INPUT-002', message: 'Missing required field' },
    ]},
  ];

  setActiveSection(sectionId: string): void {
    this.activeSection = sectionId;
  }

  setActivePlatform(platformId: string): void {
    this.activePlatform = platformId;
  }

  toggleEndpoint(endpointId: string): void {
    if (this.expandedEndpoints.has(endpointId)) {
      this.expandedEndpoints.delete(endpointId);
    } else {
      this.expandedEndpoints.add(endpointId);
    }
  }

  isEndpointExpanded(endpointId: string): boolean {
    return this.expandedEndpoints.has(endpointId);
  }

  getMethodClass(method: string): string {
    const classes: Record<string, string> = {
      GET: 'method-get',
      POST: 'method-post',
      PUT: 'method-put',
      PATCH: 'method-patch',
      DELETE: 'method-delete',
    };
    return classes[method] || '';
  }

  getEndpointsByCategory(category: string): any[] {
    return this.apiEndpoints.filter((e) => e.category === category);
  }

  getUniqueCategories(): string[] {
    return [...new Set(this.apiEndpoints.map((e) => e.category))];
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key);
    return translated === key ? fallback : translated;
  }

  getCategoryLabel(category: string): string {
    return this.translateOrFallback(`sdk-docs.api-reference.categories.${category}`, category);
  }

  getErrorCategoryLabel(category: string): string {
    return this.translateOrFallback(`sdk-docs.error-handling.categories.${category}`, `${category} Errors`);
  }

  formatJson(obj: any): string {
    return JSON.stringify(obj, null, 2);
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
  }
}
